import { useEffect } from "react";
import { fetchBranding } from "@/lib/api";

/**
 * Snitcher tracking snippet injector.
 *
 * The admin pastes the full `<script>…</script>` block from Snitcher into
 * `SNITCHER_SNIPPET` in Settings. We fetch it via /api/branding (public),
 * extract the JS body, and append a single <script> element to <head>.
 *
 * Idempotent — the script only mounts once per page load, even if the
 * component re-renders. Removes cleanly on unmount so switching between
 * pages doesn't leave orphan tags around.
 */
export default function SnitcherEmbed() {
  useEffect(() => {
    let mounted = true;
    let scriptEl = null;

    fetchBranding()
      .then((brand) => {
        if (!mounted) return;
        const raw = (brand?.snitcher_snippet || "").trim();
        if (!raw) return;
        if (document.getElementById("cd-snitcher-script")) return; // already mounted this page

        // Extract the inner JS if the admin pasted a full <script>…</script>.
        // If they pasted raw JS, use it directly.
        const inner = raw.match(/<script[^>]*>([\s\S]*?)<\/script>/i);
        const code = inner ? inner[1] : raw;

        // If Snitcher gave the user a `src="..."` script tag (async loader),
        // preserve that instead of inlining.
        const srcMatch = raw.match(/<script[^>]*\bsrc=["']([^"']+)["'][^>]*>/i);
        scriptEl = document.createElement("script");
        scriptEl.id = "cd-snitcher-script";
        if (srcMatch) {
          scriptEl.src = srcMatch[1];
          scriptEl.async = true;
        } else {
          scriptEl.text = code;
        }
        document.head.appendChild(scriptEl);
      })
      .catch(() => { /* silent — never break the page if branding fails */ });

    return () => {
      mounted = false;
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, []);

  return null;
}
